import React, { useRef, useState, useEffect, useMemo } from 'react';
import ReactPlayer from 'react-player';
import { useParams, useLocation } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { detectPlatform, getOEmbedUrl } from '../utils/platformUtils';
import API_BASE_URL from '../config';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// Trade a public site_id for a short-lived widget token. POST, not GET: the
// browser sends the Origin header on same-origin POSTs but not GETs, and the
// backend origin-gates minting per tenant. Demo result links carry site_id
// because /api/demo-configs no longer exposes customer keys.
async function mintWidgetToken(siteId) {
  const r = await fetch(`${API_BASE_URL}/api/widget-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ site_id: siteId }),
  });
  if (!r.ok) throw new Error('token ' + r.status);
  return (await r.json()).token;
}

function LandingPage() {
  const { videoId, privacyHash } = useParams();
  const location = useLocation();
  const fullVideoId = privacyHash ? `${videoId}/${privacyHash}` : videoId;
  
  console.log('DEBUG: videoId:', videoId);
  console.log('DEBUG: privacyHash:', privacyHash);
  console.log('DEBUG: fullVideoId:', fullVideoId);

  // Video processing
  const VIDEO_ID = fullVideoId || videoId;
  const BASE_VIDEO_ID = VIDEO_ID.split('/')[0];
  const PRIVACY_HASH = VIDEO_ID.includes('/') ? VIDEO_ID.split('/')[1] : null;
  const platform = detectPlatform(VIDEO_ID);
  
  // Generate video URLs
  let VIDEO_URL;
  let VIMEO_PLAYER_URL;
  
  if (platform === 'vimeo') {
    VIDEO_URL = `https://vimeo.com/${BASE_VIDEO_ID}`;
    if (PRIVACY_HASH) {
      VIDEO_URL = `https://vimeo.com/${BASE_VIDEO_ID}/${PRIVACY_HASH}`;
      // Use the correct Vimeo embed format with privacy hash as query parameter
      VIMEO_PLAYER_URL = `https://player.vimeo.com/video/${BASE_VIDEO_ID}?h=${PRIVACY_HASH}`;
    } else {
      VIMEO_PLAYER_URL = `https://player.vimeo.com/video/${BASE_VIDEO_ID}`;
    }
  } else {
    VIDEO_URL = `https://www.youtube.com/watch?v=${VIDEO_ID}`;
    VIMEO_PLAYER_URL = null;
  }

  console.log('Platform:', platform);
  console.log('Video ID:', VIDEO_ID);
  console.log('Base Video ID:', BASE_VIDEO_ID);
  console.log('Privacy Hash:', PRIVACY_HASH);
  console.log('Video URL:', VIDEO_URL);
  if (VIMEO_PLAYER_URL) {
    console.log('Vimeo Player URL:', VIMEO_PLAYER_URL);
  }

  const API_TRANSCRIPT_URL = `${API_BASE_URL}/api/transcript?video_id=${BASE_VIDEO_ID}`;
  const API_SUMMARY_URL = `${API_BASE_URL}/api/summary?video_id=${BASE_VIDEO_ID}`;
  
  // Debug logging for video ID extraction
  console.log('DEBUG: API URLs constructed:');
  console.log('API_TRANSCRIPT_URL:', API_TRANSCRIPT_URL);
  console.log('API_SUMMARY_URL:', API_SUMMARY_URL);
  
  const [transcript, setTranscript] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [currentTime, setCurrentTime] = useState(0);
  const [pdfPanelOpen, setPdfPanelOpen] = useState(false);
  const playerRef = useRef(null);
  const transcriptRefs = useRef([]);

  // Add summary state
  const [summary, setSummary] = useState(null);
  const [chatSuggestions, setChatSuggestions] = useState([]);
  const [videoTitle, setVideoTitle] = useState('');
  const [publishDate, setPublishDate] = useState(null);

  // Video poster overlay state
  const [isPlaying, setIsPlaying] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [showPoster, setShowPoster] = useState(true);
  // Add player ready state
  const [playerReady, setPlayerReady] = useState(false);
  // Add pending seek state for when we need to seek after player is ready
  const [pendingSeek, setPendingSeek] = useState(null);
  // Add seeking state to prevent onProgress from overriding currentTime during seeks
  const [isSeeking, setIsSeeking] = useState(false);

  // PDF-related state
  const [pdfs, setPdfs] = useState([]);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [pdfNumPages, setPdfNumPages] = useState(null);
  const [pdfPageNumber, setPdfPageNumber] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [pdfScale, setPdfScale] = useState(1.2); // Add zoom scale state
  const [pdfFitWidth, setPdfFitWidth] = useState(false); // Add fit-to-width state
  const [pdfHighlightText, setPdfHighlightText] = useState(null);
  const [pdfHighlightDismissed, setPdfHighlightDismissed] = useState(false);
  const [pdfLanguage, setPdfLanguage] = useState('en');
  const pdfContainerRef = useRef(null);
  const pdfPageRefs = useRef([]);
  const pdfScrollTargetPage = useRef(null);
  const pdfHighlightPage = useRef(null);

  // Active PDF URL: use Spanish URL when toggled and available, otherwise English
  const activePdfUrl = pdfLanguage === 'es' && selectedPdf?.es_download_url
    ? selectedPdf.es_download_url
    : selectedPdf?.download_url;

  // Video player error and fallback state
  const [, setPlayerError] = useState(null);
  const [, setShowFallback] = useState(false);

  // --- Query Results Navigation State ---
  // Parse queryKey and t from URL
  function getQueryKeyFromQuery() {
    const params = new URLSearchParams(location.search);
    return params.get('queryKey');
  }
  function getTimestampFromQuery() {
    const params = new URLSearchParams(location.search);
    const t = params.get('t');
    if (t && !isNaN(Number(t))) {
      return Number(t);
    }
    return null;
  }
  function getPdfIdFromQuery() {
    const params = new URLSearchParams(location.search);
    return params.get('pdf_id');
  }
  function getPdfPageFromQuery() {
    const params = new URLSearchParams(location.search);
    const page = params.get('page');
    if (page && !isNaN(Number(page))) {
      return Number(page);
    }
    return 1;
  }
  function getUrlParam(name) {
    const params = new URLSearchParams(location.search);
    return params.get(name);
  }

  const queryKey = getQueryKeyFromQuery();
  const initialTimestamp = getTimestampFromQuery();
  const initialPdfId = getPdfIdFromQuery();
  const initialPdfPage = getPdfPageFromQuery();
  const urlCustomerKey = getUrlParam('customer_key');
  const urlChannelUrl = getUrlParam('channel_url');
  const urlSiteId = getUrlParam('site_id');
  const openDocs = getUrlParam('open_docs');

  // State for unified match navigation
  const [allMatches, setAllMatches] = useState([]);
  const [currentMatchIdx, setCurrentMatchIdx] = useState(null);
  const matchIdxInitialized = useRef(false);

  // Load PDFs associated with this video
  useEffect(() => {
    const loadPdfs = async () => {
      try {
        // Demo result links carry site_id (set by useSearch.getActionProps):
        // trade it for a short-lived token instead of a customer key.
        let response = null;
        if (urlSiteId) {
          try {
            const token = await mintWidgetToken(urlSiteId);
            response = await fetch(`${API_BASE_URL}/api/pdfs?video_id=${BASE_VIDEO_ID}&signed_token=${encodeURIComponent(token)}`);
          } catch (e) {
            response = null; // mint failed — fall through to the credential paths
          }
        }

        // Try to get customer_key and channel_url from the stored query results
        let customerKey = 'placeholder';
        let channelUrl = 'placeholder';

        // Explicit URL params always take priority (set by useSearch.js when widget clicks a result)
        if (urlCustomerKey && urlChannelUrl) {
          customerKey = urlCustomerKey;
          channelUrl = urlChannelUrl;
        } else if (queryKey) {
          try {
            // Extract channel_url from queryKey format: queryResults_${channelUrl}_${queryPhrase}
            const parts = queryKey.replace('queryResults_', '').split('_');
            if (parts.length >= 2) {
              channelUrl = parts[0];
            }
            // Fall back to localStorage customer key (set by CustomerView)
            const storedCustomerKey = localStorage.getItem('lastCustomerKey');
            if (storedCustomerKey) {
              customerKey = storedCustomerKey;
            }
          } catch (e) {
            console.log('Could not extract customer info from query key');
          }
        }

        if (!response || !response.ok) {
          response = await fetch(`${API_BASE_URL}/api/pdfs?video_id=${BASE_VIDEO_ID}&customer_key=${customerKey}&channel_url=${encodeURIComponent(channelUrl)}`);
        }

        // Fallback: if credentials were missing/invalid, try demo configs so that
        // landing pages reached without URL params (e.g. clicked before JS updated
        // the demo table links) still show the Documents tab. Configs are
        // credential-free, so mint a token per demo site_id.
        if (!response.ok && customerKey === 'placeholder') {
          try {
            const demoRes = await fetch(`${API_BASE_URL}/api/demo-configs`);
            if (demoRes.ok) {
              const demoConfigs = await demoRes.json();
              for (const config of Object.values(demoConfigs)) {
                if (!config.site_id || config.site_id === urlSiteId) continue;
                try {
                  const token = await mintWidgetToken(config.site_id);
                  const r = await fetch(`${API_BASE_URL}/api/pdfs?video_id=${BASE_VIDEO_ID}&signed_token=${encodeURIComponent(token)}`);
                  if (r.ok) { response = r; break; }
                } catch (e) {
                  // mint failed for this demo — try the next one
                }
              }
            }
          } catch (e) {
            // silent — demo fallback is best-effort
          }
        }

        if (response.ok) {
          const data = await response.json();
          setPdfs(data.pdfs || []);

          // Auto-open docs panel when open_docs=true
          if (openDocs === 'true' && data.pdfs && data.pdfs.length > 0) {
            setPdfPanelOpen(true);
          }

          // If we have an initial PDF ID from URL, select it
          if (initialPdfId && data.pdfs) {
            const targetPdf = data.pdfs.find(pdf => pdf.pdf_id === initialPdfId);
            if (targetPdf) {
              setSelectedPdf(targetPdf);
              setPdfPageNumber(initialPdfPage);
              pdfHighlightPage.current = initialPdfPage;
              setPdfPanelOpen(true);

              // Extract highlight text from search results
              if (queryKey) {
                try {
                  const allResults = JSON.parse(localStorage.getItem(queryKey) || '[]');
                  const flatMatches = allResults.flatMap(group => group.matches || []);
                  const match = flatMatches.find(
                    m => m.metadata?.source_type === 'pdf'
                      && m.metadata?.pdf_id === initialPdfId
                      && m.metadata?.page_number === initialPdfPage
                  );
                  if (match?.metadata?.text) setPdfHighlightText(match.metadata.text);
                } catch (e) {
                  console.log('Could not extract highlight text from search results');
                }
              }
            }
          }
        } else {
          console.log('Failed to load PDFs:', response.status);
        }
      } catch (error) {
        console.error('Error loading PDFs:', error);
      }
    };

    loadPdfs();
  }, [BASE_VIDEO_ID, initialPdfId, initialPdfPage, queryKey, urlCustomerKey, urlChannelUrl, urlSiteId, openDocs]);

  // Load query results from localStorage, build unified match list
  useEffect(() => {
    if (!queryKey) return;
    try {
      const groups = JSON.parse(localStorage.getItem(queryKey) || '[]');
      // Groups are { video_id, matches: [{metadata:{...}}] }
      const group = groups.find(g => g.video_id === BASE_VIDEO_ID);
      if (!group) return;

      const videoMatches = (group.matches || [])
        .filter(m => m.metadata?.source_type !== 'pdf')
        .map(m => ({
          type: 'video',
          start: m.metadata?.start ?? 0,
          text: m.metadata?.text_short || m.metadata?.text || '',
          metadata: m.metadata,
        }))
        .sort((a, b) => a.start - b.start);

      const pdfMatches = (group.matches || [])
        .filter(m => m.metadata?.source_type === 'pdf')
        .map(m => ({
          type: 'pdf',
          start: null,
          text: m.metadata?.text_short || m.metadata?.text || '',
          pdf_id: m.metadata?.pdf_id,
          pdf_name: m.metadata?.pdf_name,
          page_number: m.metadata?.page_number,
          metadata: m.metadata,
        }))
        .sort((a, b) =>
          (a.pdf_name || '').localeCompare(b.pdf_name || '') || (a.page_number - b.page_number)
        );

      const unified = [...videoMatches, ...pdfMatches];
      setAllMatches(unified);
      matchIdxInitialized.current = false; // reset for this load

      // Set initial active index
      if (initialTimestamp !== null) {
        const idx = unified.findIndex(m => m.type === 'video' && Math.abs(m.start - initialTimestamp) < 2);
        setCurrentMatchIdx(idx >= 0 ? idx : 0);
      } else if (initialPdfId) {
        const idx = unified.findIndex(m => m.type === 'pdf' && m.pdf_id === initialPdfId && m.page_number === initialPdfPage);
        setCurrentMatchIdx(idx >= 0 ? idx : 0);
      } else {
        setCurrentMatchIdx(unified.length > 0 ? 0 : null);
      }
    } catch (e) {
      setAllMatches([]);
      setCurrentMatchIdx(null);
    }
    // eslint-disable-next-line
  }, [queryKey, BASE_VIDEO_ID]);

  // When currentMatchIdx changes, seek video or open PDF
  useEffect(() => {
    if (currentMatchIdx === null || allMatches.length === 0) return;
    const match = allMatches[currentMatchIdx];
    if (!match) return;

    // First fire after allMatches loads: initialise transcript position only
    if (!matchIdxInitialized.current) {
      matchIdxInitialized.current = true;
      if (match.type === 'video') {
        setCurrentTime(match.start); // transcript scrolls to right line
        // pendingSeek is set by the existing initial-timestamp effect
      }
      // If PDF was the initial target, the loadPdfs useEffect already opens it
      return;
    }

    // User-driven navigation: seek/open and start playing
    if (match.type === 'video') {
      setShowPoster(false);
      setIsPlaying(true);
      if (playerReady && playerRef.current) {
        setIsSeeking(true);
        playerRef.current.seekTo(match.start, 'seconds');
        setCurrentTime(match.start);
        setTimeout(() => setIsSeeking(false), platform === 'vimeo' ? 1500 : 800);
      } else {
        setPendingSeek(match.start);
      }
    } else if (match.type === 'pdf') {
      const pdf = pdfs.find(p => p.pdf_id === match.pdf_id);
      if (pdf) {
        setSelectedPdf(pdf);
        setPdfPageNumber(match.page_number || 1);
        setPdfHighlightText(match.text || null);
        setPdfHighlightDismissed(false);
        setPdfPanelOpen(true);
        setPdfLoading(true);
        setPdfLanguage('en');
      }
    }
    // eslint-disable-next-line
  }, [currentMatchIdx]); // eslint-disable-line

  // Fetch video title from oEmbed API
  useEffect(() => {
    const oEmbedUrl = getOEmbedUrl(VIDEO_ID, platform);
    fetch(oEmbedUrl)
      .then(res => res.json())
      .then(data => {
        if (data.title) setVideoTitle(data.title);
        if (data.thumbnail_url) setThumbnailUrl(data.thumbnail_url);
      })
      .catch(() => setVideoTitle(platform === 'vimeo' ? 'Vimeo Video' : 'YouTube Video'));
  }, [VIDEO_ID, platform]);

  // Fetch transcript from API
  useEffect(() => {
    console.log('Fetching transcript from:', API_TRANSCRIPT_URL);
    fetch(API_TRANSCRIPT_URL)
      .then(res => {
        console.log('Transcript response status:', res.status, res.ok);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        console.log('Transcript data loaded:', data?.length, 'segments');
        setTranscript(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Transcript fetch error:', error);
        setLoading(false);
      });
  }, [API_TRANSCRIPT_URL]);

  // Fetch summary from API
  useEffect(() => {
    console.log('Fetching summary from:', API_SUMMARY_URL);
    fetch(API_SUMMARY_URL)
      .then(res => {
        console.log('Summary response status:', res.status, res.ok);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        console.log('Summary data loaded:', data);
        setSummary(data.summary || null);
        setChatSuggestions(data.suggestions || []);
        if (data.publish_date && data.publish_date > 0) {
          setPublishDate(data.publish_date);
        }
      })
      .catch((error) => {
        console.error('Summary fetch error:', error);
        setSummary(null);
      });
  }, [API_SUMMARY_URL]);

  // Group consecutive transcript segments into ~8-second display groups
  const displayGroups = useMemo(() => {
    if (!transcript || transcript.length === 0) return [];
    const groups = [];
    let currentGroup = { start: transcript[0].start, text: transcript[0].text };
    for (let i = 1; i < transcript.length; i++) {
      const seg = transcript[i];
      if (seg.start - currentGroup.start >= 8) {
        groups.push(currentGroup);
        currentGroup = { start: seg.start, text: seg.text };
      } else {
        currentGroup.text += ' ' + seg.text;
      }
    }
    groups.push(currentGroup);
    return groups;
  }, [transcript]);

  // Map which transcript group indices correspond to search match timestamps
  const matchedGroupIndices = useMemo(() => {
    if (allMatches.length === 0 || displayGroups.length === 0) return new Set();
    const videoMatchTimes = allMatches.filter(m => m.type === 'video').map(m => m.start);
    return new Set(
      videoMatchTimes.map(t =>
        displayGroups.reduce((acc, g, i) => (t >= g.start ? i : acc), 0)
      )
    );
  }, [allMatches, displayGroups]);

  // Find the current display group index
  const currentLineIdx = displayGroups.length > 0 ? displayGroups.reduce((acc, group, idx) => {
    if (currentTime >= group.start) return idx;
    return acc;
  }, 0) : 0;

  // Auto-scroll transcript to keep current line in view
  useEffect(() => {
    const node = transcriptRefs.current[currentLineIdx];
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentLineIdx, loading]);

  // Auto-scroll when transcript loads and we have a current time (fixes Vimeo initial scroll issue)
  useEffect(() => {
    if (displayGroups.length > 0 && currentTime > 0 && !loading) {
      const lineIdx = displayGroups.reduce((acc, group, idx) => {
        if (currentTime >= group.start) return idx;
        return acc;
      }, 0);
      const node = transcriptRefs.current[lineIdx];
      if (node) {
        console.log('Scrolling to transcript line after transcript load:', lineIdx, 'at time:', currentTime);
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [displayGroups, loading, currentTime]);

  // Special handling for Vimeo videos: Force transcript scroll when transcript loads with initial timestamp
  useEffect(() => {
    if (platform === 'vimeo' && displayGroups.length > 0 && !loading && initialTimestamp !== null) {
      const lineIdx = displayGroups.reduce((acc, group, idx) => {
        if (initialTimestamp >= group.start) return idx;
        return acc;
      }, 0);
      const node = transcriptRefs.current[lineIdx];
      if (node) {
        console.log('Vimeo: Force scrolling to transcript line after load:', lineIdx, 'at initial timestamp:', initialTimestamp);
        // Use a small delay to ensure the transcript is fully rendered
        setTimeout(() => {
          node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [platform, displayGroups, loading, initialTimestamp]);

  // Seek to timestamp on mount if t param is present (video buffers silently behind poster)
  useEffect(() => {
    // Always seek to timestamp if present, regardless of queryKey
    const t = initialTimestamp;
    if (t !== null) {
      if (playerReady && playerRef.current) {
        // Player is ready, seek silently (poster remains visible)
        console.log('Initial seek to timestamp:', t);
        setIsSeeking(true);
        playerRef.current.seekTo(t, 'seconds');
        setCurrentTime(t); // Update currentTime to scroll transcript
        // Clear seeking flag after a delay to allow onProgress to resume
        setTimeout(() => setIsSeeking(false), platform === 'vimeo' ? 1500 : 800);
      } else {
        // Player not ready yet, store the seek time for later
        console.log('Initial seek - player not ready, storing pending seek:', t);
        setPendingSeek(t);
      }
    }
    // eslint-disable-next-line
  }, [initialTimestamp, playerReady, platform]);

  // Handle pending seek when player becomes ready
  useEffect(() => {
    if (playerReady && pendingSeek !== null && playerRef.current) {
      console.log('Processing pending seek to:', pendingSeek);
      // Small delay to ensure player is fully ready
      setTimeout(() => {
        if (playerRef.current) {
          console.log('Executing pending seek to:', pendingSeek);
          setIsSeeking(true);
          playerRef.current.seekTo(pendingSeek, 'seconds');
          setCurrentTime(pendingSeek); // Update currentTime to scroll transcript
          // Clear seeking flag after a delay to allow onProgress to resume
          setTimeout(() => setIsSeeking(false), platform === 'vimeo' ? 1500 : 800);
          // Don't reset shouldAutoPlay here - let it play
        }
        setPendingSeek(null);
      }, platform === 'vimeo' ? 200 : 100);
    }
  }, [playerReady, pendingSeek, platform]);

  // Fallback: Mark player as ready after a timeout if onReady doesn't fire
  useEffect(() => {
    const fallbackTimer = setTimeout(() => {
      if (!playerReady) {
        console.log('Player ready fallback triggered');
        setPlayerReady(true);
      }
    }, 3000); // 3 second fallback

    return () => clearTimeout(fallbackTimer);
  }, [playerReady]);

  const sendChatMessage = async (question) => {
    setChatHistory(prev => [
      ...prev,
      { sender: 'user', text: question },
      { sender: 'bot', text: '', timestamp: null, isStreaming: true },
    ]);
    try {
      const res = await fetch('/api/llm_chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify({ question, video_id: BASE_VIDEO_ID }),
      });

      if (!res.ok) {
        throw new Error(`Chat request failed: ${res.status}`);
      }
      if (!res.body) {
        throw new Error('Streaming not supported by this browser.');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';
      let timestamp = null;
      let streamDone = false;

      const updateBotMessage = (text, ts, isStreaming) => {
        setChatHistory(prev => [
          ...prev.slice(0, -1),
          { sender: 'bot', text, timestamp: ts, isStreaming },
        ]);
      };

      const processEvent = (event) => {
        const line = event
          .split('\n')
          .find(l => l.startsWith('data: '));
        if (!line) return;

        const payload = line.slice(6).trim();
        if (!payload) return;

        if (payload === '[DONE]') {
          streamDone = true;
          return;
        }

        let data;
        try {
          data = JSON.parse(payload);
        } catch (parseError) {
          console.warn('Skipping malformed SSE payload', payload, parseError);
          return;
        }

        if (data.timestamp !== undefined) {
          timestamp = data.timestamp;
          updateBotMessage(accumulated, timestamp, true);
        }
        if (data.token) {
          accumulated += data.token;
          updateBotMessage(accumulated, timestamp, true);
        }
      };

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) {
          if (buffer.trim()) processEvent(buffer);
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const event of events) {
          processEvent(event);
          if (streamDone) break;
        }
      }

      updateBotMessage(accumulated || "Sorry, I couldn't find an answer.", timestamp, false);
    } catch (err) {
      console.error('Chat stream error:', err);
      setChatHistory(prev => [
        ...prev.slice(0, -1),
        { sender: 'bot', text: 'Error contacting server.', isStreaming: false },
      ]);
    }
  };

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    const question = chatInput;
    setChatInput('');
    await sendChatMessage(question);
  };

  const handleSeek = (time) => {
    setShowPoster(false);
    setIsPlaying(true);
    if (playerRef.current) {
      setIsSeeking(true);
      playerRef.current.seekTo(time, 'seconds');
      setCurrentTime(time); // Update currentTime to scroll transcript
      // Clear seeking flag after a delay to allow onProgress to resume
      setTimeout(() => setIsSeeking(false), platform === 'vimeo' ? 1500 : 800);
    }
  };

  // Reset chat handler
  const handleResetChat = () => {
    setChatHistory([]);
    setChatInput('');
  };

  // --- Match Navigation Handlers ---
  const handlePrevMatch = () => {
    if (currentMatchIdx !== null && currentMatchIdx > 0)
      setCurrentMatchIdx(prev => prev - 1);
  };
  const handleNextMatch = () => {
    if (currentMatchIdx !== null && currentMatchIdx < allMatches.length - 1)
      setCurrentMatchIdx(prev => prev + 1);
  };

  // PDF handlers
  const onDocumentLoadSuccess = ({ numPages }) => {
    setPdfNumPages(numPages);
    setPdfLoading(false);
    setPdfError(null);
    pdfPageRefs.current = new Array(numPages).fill(null);
    // Schedule scroll to target page after pages render
    if (pdfPageNumber > 1) {
      pdfScrollTargetPage.current = pdfPageNumber;
    }
  };

  const onDocumentLoadError = (error) => {
    setPdfError('Failed to load PDF');
    setPdfLoading(false);
    console.error('PDF load error:', error);
  };

  const handlePdfSelect = (pdf) => {
    setSelectedPdf(pdf);
    setPdfPageNumber(1);
    setPdfLoading(true);
    setPdfError(null);
    setPdfHighlightText(null);
    setPdfHighlightDismissed(false);
    setPdfLanguage('en');
  };

  const changePdfPage = (offset) => {
    const newPage = Math.max(1, Math.min(pdfPageNumber + offset, pdfNumPages || 1));
    setPdfPageNumber(newPage);
    scrollToPdfPage(newPage);
  };

  const scrollToPdfPage = (pageNum) => {
    const pageEl = pdfPageRefs.current[pageNum - 1];
    if (pageEl && pdfContainerRef.current) {
      pageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // PDF zoom controls
  const handleZoomIn = () => {
    setPdfScale(prev => Math.min(prev + 0.2, 3.0));
    setPdfFitWidth(false);
  };

  const handleZoomOut = () => {
    setPdfScale(prev => Math.max(prev - 0.2, 0.5));
    setPdfFitWidth(false);
  };

  // Calculate PDF width based on scale and container
  const calculatePdfWidth = () => {
    if (pdfFitWidth) {
      // Fit to available width minus padding
      return Math.min(600, window.innerWidth * 0.42);
    }
    // Scale-based width with a reasonable base size
    return Math.min(500 * pdfScale, window.innerWidth * 0.42);
  };

  // Inject spin keyframes for PDF loading spinner
  useEffect(() => {
    const styleId = 'pdf-spin-keyframes';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = '@keyframes pdf-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }';
      document.head.appendChild(style);
    }
    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, []);

  // Track visible page via IntersectionObserver as user scrolls
  useEffect(() => {
    const container = pdfContainerRef.current;
    if (!container || !pdfNumPages) return;

    const observer = new IntersectionObserver(
      (entries) => {
        let mostVisible = null;
        let maxRatio = 0;
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
            maxRatio = entry.intersectionRatio;
            mostVisible = entry.target;
          }
        });
        if (mostVisible) {
          const pageIdx = pdfPageRefs.current.indexOf(mostVisible);
          if (pageIdx >= 0) {
            setPdfPageNumber(pageIdx + 1);
          }
        }
      },
      {
        root: container,
        threshold: [0, 0.25, 0.5, 0.75, 1.0],
      }
    );

    pdfPageRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [pdfNumPages, activePdfUrl]);

  // Scroll to target page after pages render (for search result navigation)
  useEffect(() => {
    if (pdfScrollTargetPage.current && pdfNumPages) {
      const targetPage = pdfScrollTargetPage.current;
      pdfScrollTargetPage.current = null;
      // Small delay to let pages render
      setTimeout(() => {
        const pageEl = pdfPageRefs.current[targetPage - 1];
        if (pageEl && pdfContainerRef.current) {
          pageEl.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
      }, 300);
    }
  }, [pdfNumPages]);

  // Toolbar button style helper
  const toolbarBtnStyle = (disabled) => ({
    background: 'transparent',
    border: 'none',
    padding: '4px 8px',
    color: disabled ? '#c0c7d1' : '#4b5563',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '0.85em',
    fontWeight: 500,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    transition: 'background 0.15s',
  });

  // Highlight matched text in PDF text layer
  const highlightPdfText = () => {
    if (!pdfHighlightText || pdfHighlightDismissed) return;
    const container = pdfContainerRef.current;
    if (!container) return;

    // Remove existing highlights
    container.querySelectorAll('.pdf-search-highlight').forEach(el => el.remove());

    // Find the text layer for the target page (pdfPageNumber is 1-indexed)
    const pageEl = pdfPageRefs.current[pdfPageNumber - 1];
    const textLayer = pageEl
      ? pageEl.querySelector('.react-pdf__Page__textContent')
      : container.querySelector('.react-pdf__Page__textContent');
    if (!textLayer) return;

    const spans = Array.from(textLayer.querySelectorAll('span'));
    if (spans.length === 0) return;

    // Build concatenated text with span boundaries
    let concat = '';
    const boundaries = []; // { start, end, span }
    spans.forEach(span => {
      const text = span.textContent || '';
      const normalized = text.replace(/\s+/g, ' ');
      boundaries.push({ start: concat.length, end: concat.length + normalized.length, span });
      concat += normalized;
    });

    const concatLower = concat.toLowerCase();
    const searchText = (pdfHighlightText || '').replace(/\s+/g, ' ').toLowerCase();

    // Try progressively shorter prefixes
    let matchStart = -1;
    let matchLen = 0;
    for (const len of [80, 40, 20]) {
      const prefix = searchText.slice(0, len);
      if (prefix.length === 0) continue;
      const idx = concatLower.indexOf(prefix);
      if (idx !== -1) {
        matchStart = idx;
        matchLen = prefix.length;
        break;
      }
    }

    if (matchStart === -1) return;

    const matchEnd = matchStart + matchLen;
    let firstHighlight = null;

    boundaries.forEach(({ start, end, span }) => {
      if (start >= matchEnd || end <= matchStart) return;

      const rect = span.getBoundingClientRect();
      const containerRect = textLayer.getBoundingClientRect();

      const highlight = document.createElement('div');
      highlight.className = 'pdf-search-highlight';
      Object.assign(highlight.style, {
        position: 'absolute',
        left: `${rect.left - containerRect.left}px`,
        top: `${rect.top - containerRect.top}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        background: 'rgba(25, 118, 210, 0.12)',
        borderBottom: '2px solid rgba(25, 118, 210, 0.5)',
        pointerEvents: 'none',
        zIndex: 5,
      });

      textLayer.style.position = 'relative';
      textLayer.appendChild(highlight);

      if (!firstHighlight) firstHighlight = highlight;
    });

    if (firstHighlight) {
      firstHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Helper function to format timestamps with hours when needed
  const formatTimestamp = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }
  };

  // Test video access and fetch title when video ID changes
  useEffect(() => {
    if (!fullVideoId) return;

    const videoUrl = VIMEO_PLAYER_URL || VIDEO_URL;
    console.log('Testing video access for:', videoUrl);
  }, [fullVideoId, BASE_VIDEO_ID, PRIVACY_HASH, VIDEO_URL, VIMEO_PLAYER_URL]);

  const matchNavBtn = (disabled) => ({
    width: 22, height: 22, padding: 0,
    border: '1px solid #e5e7eb', borderRadius: 5,
    background: disabled ? '#f9fafb' : '#fff',
    color: disabled ? '#d1d5db' : '#374151',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '1em', fontWeight: 700,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  });

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', width: '100vw', height: '100vh', minHeight: 0, minWidth: 0, padding: 0, margin: 0, boxSizing: 'border-box', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: '#f9fbfd', color: '#232946' }}>
      
      {/* Meeting Details Section */}
      <div style={{
        background: '#fff',
        border: '1px solid #e3eaf3',
        borderRadius: 12,
        margin: '16px 24px 8px 24px',
        padding: '20px 24px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.07)',
        flex: '0 0 auto'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{
              margin: 0,
              fontSize: '1.8em',
              color: '#232946',
              fontWeight: 700,
              letterSpacing: '-0.5px'
            }}>
              {videoTitle}
            </h1>
            {publishDate && publishDate > 0 && (
              <p style={{
                margin: '6px 0 0 0',
                fontSize: '0.9em',
                color: '#6b7280',
                fontWeight: 400,
              }}>
                Published {new Date(publishDate * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            )}
          </div>
          {pdfs.length > 0 && (
            <button
              onClick={() => setPdfPanelOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: '#f4f6f9',
                border: '1px solid #e3eaf3',
                borderRadius: 8,
                padding: '8px 14px',
                cursor: 'pointer',
                fontSize: '0.9em',
                fontWeight: 500,
                color: '#4b5563',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              Documents ({pdfs.length})
            </button>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        gap: 0,
        margin: '0 24px 24px 24px',
        flex: '1 1 0',
        minHeight: 0,
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden'
      }}>
        
                {/* Video Player Section */}
        <div style={{ 
          flex: '0.6 1 0%', 
          minWidth: 0, 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column'
        }}>
          {/* Video Container */}
          <div style={{
            flex: '0 0 65%',
            minHeight: 0,
            background: '#000',
            position: 'relative'
          }}>
            {showPoster && thumbnailUrl && (
              <div
                onClick={() => { setShowPoster(false); setIsPlaying(true); }}
                style={{
                  position: 'absolute', inset: 0, zIndex: 10, cursor: 'pointer',
                  backgroundImage: `url(${thumbnailUrl})`,
                  backgroundSize: 'cover', backgroundPosition: 'center',
                }}
              >
                <div style={{
                  position: 'absolute', top: '50%', left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 68, height: 68, borderRadius: '50%',
                  background: 'rgba(0,0,0,0.7)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="white">
                    <polygon points="5,3 19,12 5,21" />
                  </svg>
                </div>
              </div>
            )}
            <ReactPlayer
              url={VIMEO_PLAYER_URL || VIDEO_URL}
              width="100%"
              height="100%"
              controls={true}
              playing={isPlaying}
              onPause={() => setIsPlaying(false)}
              ref={playerRef}
              onReady={() => {
                console.log('ReactPlayer ready with URL:', VIMEO_PLAYER_URL || VIDEO_URL);
                setPlayerReady(true);
                if (pendingSeek !== null) {
                  console.log('Executing pending seek to:', pendingSeek);
                  playerRef.current?.seekTo(pendingSeek);
                  setPendingSeek(null);
                }
              }}
              onStart={() => {
                console.log('ReactPlayer onStart - video started loading');
              }}
              onDuration={(duration) => {
                console.log('ReactPlayer onDuration - video duration loaded:', duration);
              }}
              onLoadStart={() => {
                console.log('ReactPlayer onLoadStart - video loading started');
              }}
              onProgress={(progress) => {
                // Update current time to enable transcript auto-scroll
                // Only update if we're not in the middle of seeking (prevents race conditions with manual seeks)
                if (!isSeeking) {
                  setCurrentTime(progress.playedSeconds);
                }
              }}
              onError={(error) => {
                console.error('ReactPlayer error with URL:', VIMEO_PLAYER_URL || VIDEO_URL);
                console.error('Error details:', error);
                console.error('Platform:', platform);
                console.error('Video ID:', VIDEO_ID);
                setPlayerError(error);
                
                // Show fallback after a brief delay for Vimeo
                if (platform === 'vimeo') {
                  setTimeout(() => {
                    setShowFallback(true);
                  }, 1000);
                }
              }}
              config={{
                vimeo: {
                  playerOptions: {
                    byline: false,
                    portrait: false,
                    title: false
                  }
                }
              }}
            />
          
          </div>
          
          {/* Meeting Summary below video */}
          <div style={{
            background: '#fff',
            borderTop: '1px solid #e3eaf3',
            padding: '12px 20px',
            flex: '1 1 0%',
            minHeight: 0,
            overflowY: 'auto'
          }}>
            <h3 style={{
              margin: '0 0 8px 0',
              fontSize: '1.1em',
              color: '#232946',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}>
              Meeting Summary
            </h3>
            <div style={{
              fontSize: '0.9em',
              lineHeight: 1.5,
              color: '#232946'
            }}>
              {summary === null
                ? <span style={{ color: '#888', fontStyle: 'italic' }}>Summary not available or still processing.</span>
                : Array.isArray(summary)
                  ? (
                    <ul style={{ margin: 0, paddingLeft: 18, listStyleType: 'disc' }}>
                      {summary.map((point, i) => (
                        <li key={i} style={{ marginBottom: 4 }}>{point}</li>
                      ))}
                    </ul>
                  )
                  : <span>{summary}</span>}
            </div>
          </div>
        </div>

        {/* Right Column: Transcript + AI Chat */}
        <div style={{
          flex: '1.4 1 0%',
          minWidth: 400,
          maxWidth: 800,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderLeft: '1px solid #e3eaf3'
        }}>
          {/* Transcript Panel */}
          <div style={{ flex: '0 0 45%', display: 'flex', flexDirection: 'column', minHeight: 0, borderBottom: '1px solid #e3eaf3' }}>
            <div style={{ padding: '10px 16px 0 16px', fontSize: '0.9em', fontWeight: 600, color: '#232946' }}>
              Transcript
            </div>
            {allMatches.length > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '4px 16px 6px 16px',
                borderBottom: '1px solid #f0f4f8',
              }}>
                <span style={{ fontSize: '0.72em', fontWeight: 700, color: '#9ca3af', letterSpacing: '0.07em', textTransform: 'uppercase' }}>
                  {allMatches.length} match{allMatches.length !== 1 ? 'es' : ''}
                </span>
                {currentMatchIdx !== null && (
                  <span style={{ fontSize: '0.75em', color: '#9ca3af' }}>
                    · {currentMatchIdx + 1} of {allMatches.length}
                  </span>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                  <button onClick={handlePrevMatch}
                    disabled={!currentMatchIdx}
                    style={matchNavBtn(!currentMatchIdx)}>‹</button>
                  <button onClick={handleNextMatch}
                    disabled={currentMatchIdx === allMatches.length - 1}
                    style={matchNavBtn(currentMatchIdx === allMatches.length - 1)}>›</button>
                </div>
              </div>
            )}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 12px 12px', minHeight: 0 }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Loading transcript...</div>
              ) : displayGroups.length === 0 ? (
                <div style={{ color: '#888', textAlign: 'center', padding: 40, fontStyle: 'italic' }}>Transcript not available or still processing.</div>
              ) : (
                <div style={{ padding: 0, margin: 0 }}>
                  {displayGroups.map((group, idx) => (
                    <div
                      key={idx}
                      ref={el => transcriptRefs.current[idx] = el}
                      onClick={() => handleSeek(group.start)}
                      style={{
                        marginBottom: 8,
                        cursor: 'pointer',
                        background: idx === currentLineIdx ? '#f0f0f0' : (matchedGroupIndices.has(idx) ? 'rgba(59,130,246,0.04)' : 'transparent'),
                        color: idx === currentLineIdx ? '#333' : '#232946',
                        borderRadius: 6,
                        padding: '6px 10px',
                        transition: 'background 0.2s, color 0.2s',
                        fontWeight: idx === currentLineIdx ? 'bold' : 'normal',
                        borderLeft: idx === currentLineIdx
                          ? (matchedGroupIndices.has(idx) ? '3px solid #3b82f6' : '3px solid #666')
                          : (matchedGroupIndices.has(idx) ? '3px solid rgba(59,130,246,0.4)' : '3px solid transparent'),
                      }}
                    >
                      <span style={{ color: '#888', fontSize: '0.85em', marginRight: 12, fontWeight: 600 }}>
                        {formatTimestamp(group.start)}
                      </span>
                      <span style={{ fontSize: '0.85em', lineHeight: 1.5 }}>{group.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* AI Chat Panel */}
          <div style={{ flex: '1 1 55%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Chat Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 16px 6px 16px',
            }}>
              <span style={{ fontSize: '0.9em', fontWeight: 600, color: '#232946' }}>Ask this recording</span>
              {chatHistory.length > 0 && (
                <button
                  onClick={handleResetChat}
                  style={{
                    marginLeft: 'auto',
                    background: '#f5f5f5',
                    color: '#666',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    padding: '4px 10px',
                    fontSize: '0.8em',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Reset
                </button>
              )}
            </div>

            {/* Chat History */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px', minHeight: 0 }}>
              {chatHistory.length > 0 ? (
                <div style={{ padding: '4px 0' }}>
                  {chatHistory.map((msg, idx) => (
                    <div key={idx} style={{
                      marginBottom: 10,
                      display: 'flex',
                      justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start'
                    }}>
                      <div style={{
                        background: msg.sender === 'user' ? '#333' : '#fff',
                        color: msg.sender === 'user' ? '#fff' : '#232946',
                        borderRadius: 16,
                        padding: '8px 12px',
                        maxWidth: '80%',
                        wordBreak: 'break-word',
                        fontSize: '0.88em',
                        lineHeight: 1.4,
                        border: msg.sender === 'bot' ? '1px solid #e0e0e0' : 'none',
                        boxShadow: msg.sender === 'user' ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 1px 3px rgba(0,0,0,0.1)'
                      }}>
                        {msg.isStreaming && !msg.text ? (
                          <span style={{ color: '#666' }}>...</span>
                        ) : msg.text}
                        {msg.timestamp !== undefined && msg.timestamp !== null && (
                          <span
                            style={{
                              display: 'inline',
                              marginLeft: 6,
                              color: '#1976d2',
                              cursor: 'pointer',
                              fontSize: '0.9em',
                              textDecoration: 'underline',
                            }}
                            onClick={() => handleSeek(msg.timestamp)}
                          >
                            Jump to {formatTimestamp(msg.timestamp)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: '8px 4px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {(chatSuggestions.length > 0
                      ? chatSuggestions.slice(0, 3)
                      : ['Who attended this meeting?']
                    ).map((prompt) => (
                      <button
                        key={prompt}
                        type="button"
                        onClick={() => sendChatMessage(prompt)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                          background: '#f8f9fb',
                          color: '#333',
                          border: '1px solid #eef0f4',
                          borderRadius: 10,
                          padding: '10px 14px',
                          fontSize: '0.84em',
                          fontWeight: 450,
                          cursor: 'pointer',
                          textAlign: 'left',
                          transition: 'background 0.15s, box-shadow 0.15s',
                        }}
                        onMouseOver={e => {
                          e.currentTarget.style.background = '#eff2f7';
                          e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.06)';
                        }}
                        onMouseOut={e => {
                          e.currentTarget.style.background = '#f8f9fb';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <span>{prompt}</span>
                        <span style={{ color: '#aaa', fontSize: '0.95em', marginLeft: 10 }}>→</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Chat Input - pinned at bottom */}
            <div style={{ flex: '0 0 auto', padding: '8px 12px 12px 12px' }}>
              <form onSubmit={handleChatSubmit} style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center'
              }}>
                <div style={{
                  flex: 1,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <input
                    id="chat-input-box"
                    type="text"
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    placeholder="Ask a question..."
                    style={{
                      width: '100%',
                      padding: '10px 44px 10px 14px',
                      borderRadius: 25,
                      border: '1px solid #ddd',
                      fontSize: '0.9em',
                      background: '#fff',
                      color: '#232946',
                      outline: 'none',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}
                  />
                  <button
                    type="submit"
                    style={{
                      position: 'absolute',
                      right: 6,
                      width: 30,
                      height: 30,
                      background: '#666',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '13px'
                    }}
                  >
                    &#x23CE;
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* PDF Slide-over Overlay */}
      {pdfPanelOpen && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setPdfPanelOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.3)',
              zIndex: 1000,
            }}
          />
          {/* Panel */}
          <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: '45vw',
            maxWidth: 700,
            minWidth: 400,
            height: '100vh',
            background: '#fff',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
            zIndex: 1001,
            display: 'flex',
            flexDirection: 'column',
          }}>
            {/* Panel Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              borderBottom: '1px solid #e3eaf3',
            }}>
              <span style={{ fontSize: '1.1em', fontWeight: 700, color: '#232946' }}>
                Supporting Documents ({pdfs.length})
              </span>
              <button
                onClick={() => setPdfPanelOpen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Panel Body */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, padding: '0' }}>
              {pdfs.length === 0 ? (
                <div style={{
                  color: '#888',
                  textAlign: 'center',
                  padding: 40,
                  fontStyle: 'italic'
                }}>
                  No PDFs associated with this meeting.
                </div>
              ) : (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  {/* PDF List */}
                  {!selectedPdf && (
                    <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
                      {pdfs.map((pdf) => (
                        <div
                          key={pdf.pdf_id}
                          onClick={() => handlePdfSelect(pdf)}
                          style={{
                            padding: '12px 16px',
                            marginBottom: 1,
                            background: '#fff',
                            borderRadius: 6,
                            border: 'none',
                            cursor: 'pointer',
                            transition: 'background 0.15s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                          }}
                          onMouseOver={e => { e.currentTarget.style.background = '#f4f6f9'; }}
                          onMouseOut={e => { e.currentTarget.style.background = '#fff'; }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                          </svg>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 500, color: '#1f2937', fontSize: '0.9em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {pdf.pdf_name}
                            </div>
                            <div style={{ fontSize: '0.8em', color: '#9ca3af' }}>
                              {new Date(pdf.upload_date).toLocaleDateString()}
                            </div>
                          </div>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* PDF Viewer */}
                  {selectedPdf && (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
                      {/* Unified Toolbar */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        borderBottom: '1px solid #e5e7eb',
                        background: '#fff',
                        zIndex: 10,
                        gap: 12,
                      }}>
                        {/* Breadcrumb nav */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
                          <span
                            onClick={() => { setSelectedPdf(null); setPdfHighlightText(null); setPdfHighlightDismissed(false); }}
                            style={{
                              color: '#1976d2',
                              cursor: 'pointer',
                              fontSize: '0.85em',
                              fontWeight: 500,
                              whiteSpace: 'nowrap',
                              textDecoration: 'none',
                            }}
                            onMouseOver={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                            onMouseOut={e => { e.currentTarget.style.textDecoration = 'none'; }}
                          >
                            All PDFs
                          </span>
                          <span style={{ color: '#9ca3af', fontSize: '0.85em' }}>/</span>
                          <span style={{
                            fontSize: '0.85em',
                            fontWeight: 500,
                            color: '#374151',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                          }}>
                            {selectedPdf.pdf_name}
                          </span>
                        </div>

                        {/* Controls pill */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          background: '#f4f6f9',
                          border: '1px solid #e3eaf3',
                          borderRadius: 6,
                          padding: '2px 4px',
                          flexShrink: 0,
                        }}>
                          <button onClick={() => changePdfPage(-1)} disabled={pdfPageNumber <= 1} style={toolbarBtnStyle(pdfPageNumber <= 1)} title="Previous page">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                          </button>
                          <span style={{ fontSize: '0.8em', color: '#4b5563', fontWeight: 500, minWidth: 40, textAlign: 'center', userSelect: 'none' }}>
                            {pdfPageNumber} / {pdfNumPages || '\u2013'}
                          </span>
                          <button onClick={() => changePdfPage(1)} disabled={pdfPageNumber >= pdfNumPages} style={toolbarBtnStyle(pdfPageNumber >= pdfNumPages)} title="Next page">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                          </button>

                          <div style={{ width: 1, height: 18, background: '#d1d5db', margin: '0 6px' }} />

                          <button onClick={handleZoomOut} disabled={pdfScale <= 0.5} style={toolbarBtnStyle(pdfScale <= 0.5)} title="Zoom out">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12" /></svg>
                          </button>
                          <span
                            onClick={() => { setPdfFitWidth(!pdfFitWidth); if (!pdfFitWidth) setPdfScale(1.0); }}
                            style={{ fontSize: '0.8em', color: '#4b5563', fontWeight: 500, minWidth: 38, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                            title="Toggle fit-width"
                          >
                            {pdfFitWidth ? 'Fit' : `${Math.round(pdfScale * 100)}%`}
                          </span>
                          <button onClick={handleZoomIn} disabled={pdfScale >= 3.0} style={toolbarBtnStyle(pdfScale >= 3.0)} title="Zoom in">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                          </button>
                        </div>

                        {/* EN/ES language toggle — only shown when Spanish version exists */}
                        {selectedPdf.es_download_url && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            background: '#f4f6f9',
                            border: '1px solid #e3eaf3',
                            borderRadius: 6,
                            padding: '2px',
                            flexShrink: 0,
                            gap: 2,
                          }}>
                            <button
                              onClick={() => { setPdfLanguage('en'); setPdfLoading(true); setPdfNumPages(null); }}
                              style={{
                                padding: '3px 8px',
                                fontSize: '0.75em',
                                fontWeight: pdfLanguage === 'en' ? 700 : 400,
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                                background: pdfLanguage === 'en' ? '#fff' : 'transparent',
                                color: pdfLanguage === 'en' ? '#374151' : '#9ca3af',
                                boxShadow: pdfLanguage === 'en' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                              }}
                            >EN</button>
                            <button
                              onClick={() => { setPdfLanguage('es'); setPdfLoading(true); setPdfNumPages(null); }}
                              style={{
                                padding: '3px 8px',
                                fontSize: '0.75em',
                                fontWeight: pdfLanguage === 'es' ? 700 : 400,
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                                background: pdfLanguage === 'es' ? '#fff' : 'transparent',
                                color: pdfLanguage === 'es' ? '#374151' : '#9ca3af',
                                boxShadow: pdfLanguage === 'es' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                              }}
                            >ES</button>
                          </div>
                        )}

                        {selectedPdf.download_url && (
                          <a
                            href={activePdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ flexShrink: 0, color: '#6b7280', display: 'flex', alignItems: 'center', padding: 4 }}
                            title="Download PDF"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="7 10 12 15 17 10" />
                              <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                          </a>
                        )}
                      </div>

                      {/* Search result context banner */}
                      {pdfHighlightText && !pdfHighlightDismissed && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          background: '#eff6ff',
                          borderBottom: '1px solid #bfdbfe',
                          padding: '6px 12px',
                          fontSize: '0.8em',
                          color: '#1e40af',
                        }}>
                          <span>Showing search result on this page</span>
                          <button
                            onClick={() => {
                              setPdfHighlightDismissed(true);
                              if (pdfContainerRef.current) {
                                pdfContainerRef.current.querySelectorAll('.pdf-search-highlight').forEach(el => el.remove());
                              }
                            }}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#1e40af',
                              padding: '2px 4px',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                            title="Dismiss"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                      )}

                      {/* PDF Document Container */}
                      <div
                        ref={pdfContainerRef}
                        style={{
                          flex: 1,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'flex-start',
                          overflowY: 'auto',
                          overflowX: 'auto',
                          background: '#f4f6f9',
                          minHeight: 0,
                        }}
                      >
                        {pdfLoading && (
                          <div style={{
                            padding: 40,
                            color: '#64748b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10
                          }}>
                            <div style={{
                              width: 18,
                              height: 18,
                              border: '2px solid #e5e7eb',
                              borderTop: '2px solid #1976d2',
                              borderRadius: '50%',
                              animation: 'pdf-spin 0.8s linear infinite',
                            }} />
                            <span style={{ fontSize: '0.9em' }}>Loading document...</span>
                          </div>
                        )}
                        {pdfError && (
                          <div style={{
                            padding: '16px 20px',
                            color: '#dc2626',
                            background: '#fef2f2',
                            borderRadius: 6,
                            border: '1px solid #fecaca',
                            margin: 20,
                            fontSize: '0.9em',
                          }}>
                            {pdfError}
                          </div>
                        )}
                        {activePdfUrl && !pdfError && (
                          <div style={{
                            background: '#fff',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                            margin: '8px',
                            overflow: 'hidden'
                          }}>
                            <Document
                              key={activePdfUrl}
                              file={activePdfUrl}
                              onLoadSuccess={onDocumentLoadSuccess}
                              onLoadError={onDocumentLoadError}
                              loading={
                                <div style={{
                                  padding: 40,
                                  color: '#64748b',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 10
                                }}>
                                  <div style={{
                                    width: 18,
                                    height: 18,
                                    border: '2px solid #e5e7eb',
                                    borderTop: '2px solid #1976d2',
                                    borderRadius: '50%',
                                    animation: 'pdf-spin 0.8s linear infinite',
                                  }} />
                                  <span style={{ fontSize: '0.9em' }}>Loading document...</span>
                                </div>
                              }
                            >
                              {Array.from({ length: pdfNumPages || 0 }, (_, i) => (
                                <div
                                  key={i}
                                  ref={(el) => { pdfPageRefs.current[i] = el; }}
                                  style={{ marginBottom: 8 }}
                                >
                                  <Page
                                    pageNumber={i + 1}
                                    width={calculatePdfWidth()}
                                    renderTextLayer={true}
                                    renderAnnotationLayer={true}
                                    onRenderSuccess={i + 1 === pdfHighlightPage.current ? () => setTimeout(highlightPdfText, 150) : undefined}
                                  />
                                </div>
                              ))}
                            </Document>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default LandingPage; 